import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import uiConfig from "../uiConfig";
import AiProjectBundleTextEditor from "../components/AiProjectBundleTextEditor";
import AiBuilderTimelineStrip from "../components/AiBuilderTimelineStrip";
import ErrorBanner from "../components/ErrorBanner";
import ConfirmationModal from "../components/ConfirmationModal";
import DebugTimingsModal from "../components/DebugTimingsModal";
import { getMessageForFetchError } from "../shared/utils/apiError";
import { formatDueDate, getDueDatePart } from "../shared/utils/dueDateTime";
import { getRepeatLabel } from "../shared/utils/repeatOptions";
import RepeatPatternPicker from "../components/RepeatPatternPicker";
import { buildRepeatRuleForFrequency, normalizeRepeatRule } from "../shared/utils/recurrence";
import { fieldCommitOnEnterKeyDown } from "../shared/utils/fieldCommitOnEnterKeyDown";
import { parseAiBuilderDateRangeForUi, countAiBuilderBulletTaskLines } from "../shared/utils/aiBuilderDateRangePreview";


import { apiFetch } from "../shared/api/apiFetch";

/** Initial free text so users can generate immediately or edit. Date range uses slashes so parsing succeeds. */
const DEFAULT_AI_PROJECT_BUILDER_TEXT =
  "Prep rental home for new tenant (4/1 to 4/10)\n- cut/remove dead tree\n- deep clean carpets\n- dispose of trash";

/** Free-text seed when opening from Project Management (name + optional date range + trailing " - " for new tasks). */
function buildAiBuilderExistingProjectFreeText(project) {
  const name = (project?.name != null ? String(project.name) : "").trim() || "Project";
  const startYmd = project?.start_date ? getDueDatePart(project.start_date) : "";
  const endYmd = project?.end_date ? getDueDatePart(project.end_date) : "";
  const fmtUs = (ymd) => {
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "";
    const [y, m, d] = ymd.split("-").map((x) => parseInt(x, 10));
    return `${m}/${d}/${y}`;
  };
  const su = fmtUs(startYmd);
  const eu = fmtUs(endYmd);
  if (su && eu) return `${name} (${su} to ${eu}) - `;
  if (su) return `${name} (${su}) - `;
  return `${name} - `;
}

function parseAmountInput(val) {
  const n = parseFloat(String(val).replace(/,/g, ""));
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}

/** Plain-text amount editing: allow digits and one decimal point (no spinner UI). */
function sanitizeAmountTextInput(raw) {
  let s = String(raw ?? "")
    .replace(/,/g, "")
    .replace(/[^\d.]/g, "");
  const dot = s.indexOf(".");
  if (dot === -1) return s;
  return s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, "");
}

function amountFieldDisplayString(v) {
  if (v === null || v === undefined || v === "") return "";
  return String(v);
}

function previewEditDomId(key) {
  return `ai-preview-edit-${String(key).replace(/[^a-zA-Z0-9]/g, "_")}`;
}

function repeatRuleFromPreviewRow(row, dueYmd) {
  if (row.task_repeat_rule != null) {
    return normalizeRepeatRule(row.task_repeat_rule, dueYmd);
  }
  const raw = String(row.task_repeat || "never").trim().toLowerCase() || "never";
  return buildRepeatRuleForFrequency(raw, dueYmd);
}

/** Normalize project id from import JSON (string, BSON $oid, ObjectId). */
function normalizeBundleProjectId(raw) {
  if (raw == null || raw === "") return "";
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "object") {
    if (typeof raw.$oid === "string") return raw.$oid.trim();
    if (raw._id != null) return normalizeBundleProjectId(raw._id);
  }
  const s = String(raw).trim();
  return s && s !== "[object Object]" ? s : "";
}

/** Calendar display for YYYY-MM-DD project dates (UTC date-only). */
function formatProjectYmdUs(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "";
  return formatDueDate(`${ymd}T00:00:00.000Z`);
}

/** Single-line preview source label (matches toggle names); hover for color meaning. */
function PreviewSourceCallout({ meta }) {
  if (!meta || typeof meta !== "object") return null;
  const previewSource = meta.previewSource;
  const assistantFallback = meta.assistantFallback === true;
  const base = {
    marginTop: 14,
    marginBottom: 0,
    padding: "10px 12px",
    borderRadius: 6,
    fontSize: 14,
  };
  let style;
  let headline;
  /** Native tooltip + screen reader detail */
  let detailTip;

  if (previewSource === "assistant") {
    style = { ...base, background: "#d1e7dd", border: "1px solid #a3cfbb", color: "#0f5132" };
    headline = "Gen AI Assistant";
    detailTip =
      "Green: this preview was built by the Gen AI assistant on the server from your text. Hover the preview mode options above for how each mode works.";
  } else if (previewSource === "heuristic") {
    headline = "Heuristic Parser";
    if (assistantFallback) {
      style = { ...base, background: "#fff3cd", border: "1px solid #ffecb5", color: "#664d03" };
      detailTip =
        "Amber: you had Gen AI Assistant selected, but the assistant was unavailable, not configured, busy, or returned an error. The server used the heuristic parser automatically so you can keep working. Try Generate again later, or choose Heuristic Parser to compare.";
    } else {
      style = { ...base, background: "#e7f1ff", border: "1px solid #b6d4fe", color: "#084298" };
      detailTip =
        "Blue: you chose Heuristic Parser. This preview uses rule-based parsing only (dates, commas, colons, line breaks)—no Gen AI call. Select Gen AI Assistant and generate again to compare.";
    }
  } else {
    style = { ...base, background: "#e2e8f0", border: "1px solid #cbd5e1", color: "#334155" };
    headline = "Preview ready";
    detailTip = "Preview is ready. Review and edit tasks before creating.";
  }

  return (
    <div
      role="status"
      style={{ ...style, cursor: "help" }}
      title={detailTip}
      aria-label={`${headline}. ${detailTip}`}
    >
      <strong>{headline}</strong>
    </div>
  );
}

function AIProjectBuilder() {
  const serverUrl = uiConfig.serverInfo.serverUrl;
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
  const account_id = userInfo?.account_id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const existingProjectId = useMemo(() => {
    const raw = searchParams.get("existingProjectId");
    return raw ? String(raw).trim() : "";
  }, [searchParams]);

  const [freeText, setFreeText] = useState(DEFAULT_AI_PROJECT_BUILDER_TEXT);

  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [generated, setGenerated] = useState(null); // { rows, csv, meta }
  const [importResult, setImportResult] = useState(null); // server import response
  const [clientTimings, setClientTimings] = useState(null); // set when server returns debug meta; shown in DebugTimingsModal only
  const [debugTimingsOpen, setDebugTimingsOpen] = useState(false);
  /** Editable copy of server rows (task, notes, due, amount, repeat); synced when preview is generated. */
  const [editedPreviewRows, setEditedPreviewRows] = useState([]);
  /** Review bar: project range; kept in sync with row `project_*_date` fields (YYYY-MM-DD) for import. */
  const [reviewProjectStartYmd, setReviewProjectStartYmd] = useState("");
  const [reviewProjectEndYmd, setReviewProjectEndYmd] = useState("");
  /** Review bar: display name; kept in sync with row `project` for import. */
  const [reviewProjectName, setReviewProjectName] = useState("");
  /** New-project classification; existing-project mode never changes the stored project type. */
  const [isTemplate, setIsTemplate] = useState(false);
  /** Which preview field is in edit mode: meta-name | meta-start | meta-end | r{n}-task | … */
  const [previewEditKey, setPreviewEditKey] = useState(null);
  /** POST /generateProjectBundleFromText: gemini = assistant with fallback; heuristic = parser only */
  const [previewMode, setPreviewMode] = useState("gemini");

  /** Last successful import response; OK handler reads this so navigation never loses ids if state batching races. */
  const importSuccessSnapshotRef = useRef(null);
  /** Text area: bullet toggle from actions column */
  const aiEditorRef = useRef(null);

  /** Sent to /generateProjectBundleFromText so preview rows use the exact stored project name (existing-project flow). */
  const [generateBundleProjectNameOverride, setGenerateBundleProjectNameOverride] = useState("");

  useEffect(() => {
    setPreviewEditKey(null);
  }, [generated]);

  useEffect(() => {
    if (!account_id) return;
    if (!existingProjectId) {
      setGenerateBundleProjectNameOverride("");
      return;
    }
    const snap = location.state?.aiBuilderExistingProject;
    if (snap && String(snap._id) === existingProjectId) {
      setError(null);
      setFreeText(buildAiBuilderExistingProjectFreeText(snap));
      setGenerateBundleProjectNameOverride((snap.name != null ? String(snap.name) : "").trim());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiFetch(serverUrl + "/getProject", {
          method: "POST",
          body: JSON.stringify({ account_id }),
        });
        const data = await resp.json().catch(() => null);
        if (cancelled || !Array.isArray(data)) return;
        const p = data.find((x) => String(x?._id) === existingProjectId);
        if (!p) {
          setError("Project not found or no longer available.");
          return;
        }
        setError(null);
        setFreeText(buildAiBuilderExistingProjectFreeText(p));
        setGenerateBundleProjectNameOverride((p.name != null ? String(p.name) : "").trim());
      } catch (err) {
        if (!cancelled) setError(getMessageForFetchError(err));
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally omit location.state: only the initial navigation snapshot is needed when it matches existingProjectId.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- read location.state once per id/account change
  }, [account_id, existingProjectId, serverUrl]);

  useEffect(() => {
    if (!previewEditKey) return;
    const id = previewEditDomId(previewEditKey);
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (!el) return;
      el.focus();
      try {
        if (el instanceof HTMLInputElement && el.type === "text") el.select();
        else if (el instanceof HTMLTextAreaElement) el.select();
      } catch (_) {
        /* ignore */
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [previewEditKey]);

  /** Escape clears edit mode; Enter commits like blur (textarea: Ctrl/Cmd+Enter for newline). */
  const previewEditableKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setPreviewEditKey(null);
      e.currentTarget.blur();
      return;
    }
    fieldCommitOnEnterKeyDown(e);
  }, []);

  useEffect(() => {
    const rows = generated?.rows;
    if (!Array.isArray(rows)) {
      setEditedPreviewRows([]);
      setReviewProjectStartYmd("");
      setReviewProjectEndYmd("");
      setReviewProjectName("");
      return;
    }
    const mapped = rows.map((r) => ({ ...r }));
    setEditedPreviewRows(mapped);
    const m = generated?.meta || {};
    const metaName = (m.projectName && String(m.projectName).trim()) || "";
    const rowName = (mapped[0]?.project != null && String(mapped[0].project).trim()) || "";
    setReviewProjectName(metaName || rowName);
    const metaStart = (m.projectStart && String(m.projectStart).trim().slice(0, 10)) || "";
    const metaEnd = (m.projectEnd && String(m.projectEnd).trim().slice(0, 10)) || "";
    const rowStart = getDueDatePart(mapped[0]?.project_start_date) || "";
    const rowEnd = getDueDatePart(mapped[0]?.project_end_date) || "";
    const ymdRe = /^\d{4}-\d{2}-\d{2}$/;
    setReviewProjectStartYmd(ymdRe.test(metaStart) ? metaStart : rowStart);
    setReviewProjectEndYmd(ymdRe.test(metaEnd) ? metaEnd : rowEnd);
  }, [generated]);

  function updateReviewProjectName(name) {
    const v = name != null ? String(name) : "";
    setReviewProjectName(v);
    setEditedPreviewRows((prev) => prev.map((r) => ({ ...r, project: v })));
  }

  function updateReviewProjectStartYmd(ymd) {
    const v = ymd || "";
    setReviewProjectStartYmd(v);
    setEditedPreviewRows((prev) => prev.map((r) => ({ ...r, project_start_date: v })));
  }

  function updateReviewProjectEndYmd(ymd) {
    const v = ymd || "";
    setReviewProjectEndYmd(v);
    setEditedPreviewRows((prev) => prev.map((r) => ({ ...r, project_end_date: v })));
  }

  const previewRows = useMemo(() => {
    return editedPreviewRows.filter((r) => (r.task || "").trim());
  }, [editedPreviewRows]);

  function updatePreviewRow(rowIndex, patch) {
    setEditedPreviewRows((prev) => {
      if (rowIndex < 0 || rowIndex >= prev.length) return prev;
      const next = [...prev];
      next[rowIndex] = { ...next[rowIndex], ...patch };
      return next;
    });
  }

  async function handleGeneratePreview() {
    if (!account_id) return;
    const text = (freeText || "").trim();
    if (!text) {
      setError("Paste your project + tasks text first.");
      return;
    }
    const startMs = performance.now();
    setClientTimings(null);
    setGenerating(true);
    setSubmitting(false);
    setError(null);
    setGenerated(null);
    setImportResult(null);
    importSuccessSnapshotRef.current = null;
    try {
      const reqStart = performance.now();
      const resp = await apiFetch(serverUrl + "/generateProjectBundleFromText", {
        method: "POST",
        body: JSON.stringify({
          account_id,
          created_by: userInfo?._id || "",
          text,
          projectNameOverride: generateBundleProjectNameOverride,
          previewMode,
        }),
      });
      const afterHeaders = performance.now();
      const requestMs = Math.max(0, Math.round(afterHeaders - reqStart));
      const data = await resp.json().catch(() => null);
      const afterJson = performance.now();
      const jsonMs = Math.max(0, Math.round(afterJson - afterHeaders));
      const totalMs = Math.max(0, Math.round(afterJson - startMs));
      setClientTimings({ requestMs, jsonMs, totalMs });
      if (!resp.ok) {
        const msg = data?.message || "Generate preview failed.";
        const errs = Array.isArray(data?.errors) ? data.errors : [];
        setError([msg, ...errs.slice(0, 10)].join("\n"));
        return;
      }
      setGenerated(data);
      if (data?.meta?.debugTimings === true) {
        setDebugTimingsOpen(true);
      }
    } catch (err) {
      setError(getMessageForFetchError(err));
    } finally {
      setGenerating(false);
    }
  }

  async function handleCreate() {
    if (!account_id) return;
    if (previewRows.length === 0) return;
    setSubmitting(true);
    setError(null);
    setImportResult(null);
    importSuccessSnapshotRef.current = null;
    try {
      const resp = await apiFetch(serverUrl + "/importProjectBundle", {
        method: "POST",
        body: JSON.stringify({
          account_id,
          created_by: userInfo?._id || "",
          rows: editedPreviewRows.map((r) => {
            const dueYmd = getDueDatePart(r.task_due_date) || "";
            const repeatRule = repeatRuleFromPreviewRow(r, dueYmd);
            return {
              ...r,
              task_amount: parseAmountInput(r.task_amount),
              task_repeat_rule: repeatRule,
              task_repeat: getRepeatLabel(repeatRule),
            };
          }),
          ...(!existingProjectId ? { is_template: isTemplate } : {}),
          ...(existingProjectId ? { existing_project_id: existingProjectId } : {}),
        }),
      });
      const data = await resp.json().catch(() => null);
      if (!resp.ok) {
        const msg = data?.message || "Create failed.";
        const errs = Array.isArray(data?.errors) ? data.errors : [];
        setError([msg, ...errs.slice(0, 10)].join("\n"));
        return;
      }
      importSuccessSnapshotRef.current = data;
      setImportResult(data);
    } catch (err) {
      setError(getMessageForFetchError(err));
    } finally {
      setSubmitting(false);
    }
  }

  function handleStartOver() {
    setFreeText(DEFAULT_AI_PROJECT_BUILDER_TEXT);
    setGenerateBundleProjectNameOverride("");
    setIsTemplate(false);
    setPreviewMode("gemini");
    setGenerating(false);
    setSubmitting(false);
    setError(null);
    setGenerated(null);
    setImportResult(null);
    importSuccessSnapshotRef.current = null;
    setClientTimings(null);
    setDebugTimingsOpen(false);
    if (existingProjectId) {
      navigate("/AIProjectBuilder", { replace: true });
    }
  }

  const importSuccessModalMessage = useMemo(() => {
    const imported = importResult?.imported || null;
    if (!imported) return "";
    const rowIssues = Array.isArray(importResult?.errors) ? importResult.errors : [];
    const isExisting = Boolean(existingProjectId);
    const lines = [
      isExisting ? "Tasks were added to the project." : "Your project and tasks were imported.",
      "",
      `Rows processed: ${imported.rowsProcessed ?? 0}`,
      `Projects touched: ${imported.projectsTouched ?? 0}`,
      `Tasks touched: ${imported.tasksTouched ?? 0}`,
    ];
    if (rowIssues.length > 0) {
      lines.push("", "Some rows had issues (those rows were skipped):");
      rowIssues.slice(0, 8).forEach((msg) => lines.push(`• ${msg}`));
      if (rowIssues.length > 8) lines.push(`… and ${rowIssues.length - 8} more`);
    }
    lines.push("", "Open the task list for this project to review.");
    return lines.join("\n");
  }, [importResult, existingProjectId]);

  const handleImportSuccessAck = useCallback(() => {
    const snapshot = importSuccessSnapshotRef.current;
    const imp = snapshot?.imported ?? importResult?.imported;
    const candidates = [
      imp?.primaryProjectId,
      imp?.existingProjectId,
      ...(Array.isArray(imp?.touchedProjectIds) ? imp.touchedProjectIds : []),
    ];
    const pidFromImport = candidates.map(normalizeBundleProjectId).find(Boolean) || "";
    const pid = (pidFromImport || normalizeBundleProjectId(existingProjectId) || "").trim();
    importSuccessSnapshotRef.current = null;
    setImportResult(null);
    setGenerated(null);
    setError(null);
    if (pid) {
      navigate(`/Task?projectId=${encodeURIComponent(pid)}`);
    } else {
      navigate("/Task");
    }
  }, [importResult, existingProjectId, navigate]);

  const builderTimelinePreview = useMemo(() => {
    return {
      range: parseAiBuilderDateRangeForUi(freeText),
      bulletTaskCount: countAiBuilderBulletTaskLines(freeText),
    };
  }, [freeText]);

  if (!account_id) {
    return (
      <div className="page-container">
        <div className="panel">User session not found. Please log in again.</div>
      </div>
    );
  }

  const done = importResult && typeof importResult === "object" && importResult.success;
  const meta = generated?.meta || {};
  const isExistingProjectMode = Boolean(existingProjectId);

  return (
    <div className="page-container ai-builder-page">
      <div className="page-top">
        <div className="page-header">
          <h2 className="page-title">AI Project Builder</h2>
          <p className="page-subtitle" style={{ marginBottom: "16px", lineHeight: 1.5 }}>
            {isExistingProjectMode
              ? "Add tasks to this existing project. Your text is prefilled from the project—edit it, pick how to interpret it, then generate a preview and import."
              : "Paste your project and tasks. Our AI suggests a structure—or switch to literal parsing when you want predictable, local rules."}
          </p>
        </div>
      </div>

      <ErrorBanner message={error} onDismiss={() => setError(null)} />

      {!done && (
        <div className="panel ai-builder-panel">
          <div className="ai-builder-input-layout">
            <div className="ai-builder-input-layout__editor">
              <div className="ai-builder-editor-shell">
                <label className="ai-builder-editor-caption" htmlFor="ai-project-builder-free-text">
                  Project + tasks (structured text)
                </label>
                <AiProjectBundleTextEditor
                  ref={aiEditorRef}
                  id="ai-project-builder-free-text"
                  value={freeText}
                  onChange={setFreeText}
                  disabled={generating || submitting}
                  minRows={7}
                  aria-label="Project name, date range, and task list"
                />
              </div>
              <div className="ai-builder-input-layout__hints">
                <ul className="ai-builder-hint-list">
                  <li>
                    <strong>Project:</strong> Use the first line (or text before a colon).
                  </li>
                  <li>
                    <strong>Tasks:</strong> Use bullets (<code>-</code> or <code>*</code>). Press <strong>Enter</strong> to continue the same bullet;{" "}
                    <strong>Shift+Enter</strong> for a plain new line.
                  </li>
                  <li>
                    <strong>Dates:</strong> Add a range (e.g. <strong>4/1/2026 to 4/15/2026</strong>) so due dates can spread across the window.
                  </li>
                </ul>
              </div>
            </div>
            <div className="ai-builder-input-layout__actions" role="group" aria-label="Formatting and preview actions">
              <div>
                <div className="ai-builder-segmented-label" id="ai-builder-creation-method-label">
                  Creation method
                </div>
                <div
                  className="ai-builder-segmented"
                  role="radiogroup"
                  aria-labelledby="ai-builder-creation-method-label"
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={previewMode === "gemini"}
                    className="ai-builder-segmented__btn"
                    disabled={generating || submitting}
                    title="Gen AI Assistant when configured; otherwise the server falls back to the heuristic parser. Green preview bar = AI; amber = fallback."
                    onClick={() => setPreviewMode("gemini")}
                  >
                    <span className="ai-builder-segmented__icon" aria-hidden>
                      ✨
                    </span>
                    Gen AI Assistant
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={previewMode === "heuristic"}
                    className="ai-builder-segmented__btn"
                    disabled={generating || submitting}
                    title="Heuristic parser only—no Gen AI call. Blue preview bar = parser output."
                    onClick={() => setPreviewMode("heuristic")}
                  >
                    <span className="ai-builder-segmented__icon" aria-hidden>
                      ≡
                    </span>
                    Heuristic Parser
                  </button>
                </div>
              </div>

              <AiBuilderTimelineStrip range={builderTimelinePreview.range} bulletTaskCount={builderTimelinePreview.bulletTaskCount} />

              <div className="ai-builder-actions-row">
                <button
                  type="button"
                  className="btn btn-secondary ai-builder-action-btn ai-builder-action-btn--secondary"
                  onClick={() => aiEditorRef.current?.toggleBullet()}
                  disabled={generating || submitting}
                  title="Add or remove a leading bullet (-) on each line in the selection"
                >
                  • Bullet list
                </button>
                <button
                  type="button"
                  className="btn btn-secondary ai-builder-action-btn ai-builder-action-btn--secondary"
                  onClick={handleStartOver}
                  disabled={generating || submitting}
                >
                  Clear
                </button>
              </div>
              <button
                type="button"
                className="btn btn-primary ai-builder-action-btn ai-builder-action-btn--primary ai-builder-generate-btn"
                onClick={handleGeneratePreview}
                disabled={generating || submitting || !(freeText || "").trim()}
              >
                {generating ? "Preparing preview…" : "Generate preview"}
              </button>
            </div>
          </div>

          {generated && (
            <>
              <PreviewSourceCallout meta={generated?.meta} />
              <div
                className="panel panel-row"
                style={{ gap: 24, marginTop: 16, marginBottom: 12, flexWrap: "wrap", overflowX: "auto", alignItems: "center" }}
              >
                <span className="toolbar-label ai-preview-read-toolbar-project-row">
                  <span>Project:</span>
                  {previewEditKey === "meta-name" ? (
                    <input
                      id={previewEditDomId("meta-name")}
                      type="text"
                      className="input input-full"
                      style={{ fontSize: 13, padding: "4px 8px", minWidth: 160, maxWidth: 280, width: "auto", flexShrink: 0 }}
                      value={reviewProjectName}
                      onChange={(e) => updateReviewProjectName(e.target.value)}
                      onBlur={() => setPreviewEditKey(null)}
                      onKeyDown={previewEditableKeyDown}
                      placeholder="Project name"
                      aria-label="Project name"
                    />
                  ) : (
                    <button
                      type="button"
                      className={`ai-preview-read ai-preview-read-toolbar-name ${
                        !(reviewProjectName || "").trim() ? "ai-preview-read-muted" : ""
                      }`}
                      onClick={() => setPreviewEditKey("meta-name")}
                    >
                      {(reviewProjectName || "").trim() || "Click to set name"}
                    </button>
                  )}
                </span>
                <span className="toolbar-label ai-preview-read-toolbar-dates-row">
                  <span>Project dates:</span>
                  {previewEditKey === "meta-start" ? (
                    <input
                      id={previewEditDomId("meta-start")}
                      type="date"
                      className="input input-full"
                      style={{ fontSize: 13, padding: "4px 8px", maxWidth: "11rem", width: "auto", flexShrink: 0 }}
                      value={reviewProjectStartYmd}
                      onChange={(e) => updateReviewProjectStartYmd(e.target.value)}
                      onBlur={() => setPreviewEditKey(null)}
                      onKeyDown={previewEditableKeyDown}
                      aria-label="Project start date"
                    />
                  ) : (
                    <button
                      type="button"
                      className={`ai-preview-read ai-preview-read-toolbar-date ai-preview-read-toolbar-inline ${
                        !reviewProjectStartYmd ? "ai-preview-read-muted" : ""
                      }`}
                      onClick={() => setPreviewEditKey("meta-start")}
                    >
                      {formatProjectYmdUs(reviewProjectStartYmd) || "—"}
                    </button>
                  )}
                  <span style={{ color: "#6c757d" }} aria-hidden>
                    →
                  </span>
                  {previewEditKey === "meta-end" ? (
                    <input
                      id={previewEditDomId("meta-end")}
                      type="date"
                      className="input input-full"
                      style={{ fontSize: 13, padding: "4px 8px", maxWidth: "11rem", width: "auto", flexShrink: 0 }}
                      value={reviewProjectEndYmd}
                      onChange={(e) => updateReviewProjectEndYmd(e.target.value)}
                      onBlur={() => setPreviewEditKey(null)}
                      onKeyDown={previewEditableKeyDown}
                      aria-label="Project end date"
                    />
                  ) : (
                    <button
                      type="button"
                      className={`ai-preview-read ai-preview-read-toolbar-date ai-preview-read-toolbar-inline ${
                        !reviewProjectEndYmd ? "ai-preview-read-muted" : ""
                      }`}
                      onClick={() => setPreviewEditKey("meta-end")}
                    >
                      {formatProjectYmdUs(reviewProjectEndYmd) || "—"}
                    </button>
                  )}
                </span>
                {!isExistingProjectMode && (
                  <label className="toolbar-label" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                    <input
                      type="checkbox"
                      checked={isTemplate}
                      onChange={(e) => setIsTemplate(e.target.checked)}
                      disabled={submitting}
                      style={{ marginRight: 6 }}
                    />
                    Project template
                  </label>
                )}
                <span className="toolbar-label" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                  Rows: <strong style={{ color: "#0d6efd" }}>{previewRows.length}</strong>
                </span>
              </div>

              {editedPreviewRows.length > 0 && (
                <>
                  <p className="panel-title" style={{ marginBottom: 8 }}>
                    {isExistingProjectMode
                      ? "Preview — tasks to add to this project (edit before importing)"
                      : "Preview — tasks to be created (edit before creating)"}
                  </p>
                  <div className="table-scroll" style={{ maxHeight: "340px", marginBottom: 16 }}>
                    <div className="table-wrapper">
                      <table className="ai-project-builder-preview-table">
                        <thead>
                          <tr>
                            <th className="text-center ai-preview-col-num">#</th>
                            <th className="ai-preview-col-task">Task</th>
                            <th className="ai-preview-col-notes">Notes</th>
                            <th className="ai-preview-col-due">Due date</th>
                            <th className="ai-preview-col-amount">Amount($)</th>
                            <th className="ai-preview-col-repeat">Repeat</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editedPreviewRows.map((r, rowIndex) => {
                            if (!(r.task || "").trim()) return null;
                            const displayNum =
                              editedPreviewRows.slice(0, rowIndex + 1).filter((x) => (x.task || "").trim()).length;
                            const dueYmd = getDueDatePart(r.task_due_date) || "";
                            const repeatRule = repeatRuleFromPreviewRow(r, dueYmd);
                            const kTask = `r${rowIndex}-task`;
                            const kNotes = `r${rowIndex}-notes`;
                            const kDue = `r${rowIndex}-due`;
                            const kAmt = `r${rowIndex}-amt`;
                            const kRep = `r${rowIndex}-rep`;
                            const notesTrim = (r.task_notes != null ? String(r.task_notes) : "").trim();
                            const dueRead =
                              formatDueDate(r.task_due_date) || (dueYmd ? formatProjectYmdUs(dueYmd) : "") || "";
                            const amountRead = String(parseAmountInput(r.task_amount));
                            return (
                              <tr key={rowIndex}>
                                <td className="panel-hint-inline ai-preview-col-num text-center">
                                  <span className="ai-preview-row-num-inner">{displayNum}</span>
                                </td>
                                <td className="ai-preview-col-task">
                                  {previewEditKey === kTask ? (
                                    <input
                                      id={previewEditDomId(kTask)}
                                      type="text"
                                      className="input input-full"
                                      style={{ fontSize: 13, padding: "6px 8px" }}
                                      value={r.task || ""}
                                      onChange={(e) => updatePreviewRow(rowIndex, { task: e.target.value })}
                                      onBlur={() => setPreviewEditKey(null)}
                                      onKeyDown={previewEditableKeyDown}
                                      aria-label="Task name"
                                    />
                                  ) : (
                                    <button
                                      type="button"
                                      className="ai-preview-read"
                                      onClick={() => setPreviewEditKey(kTask)}
                                    >
                                      {(r.task || "").trim() || "—"}
                                    </button>
                                  )}
                                </td>
                                <td className="ai-preview-col-notes">
                                  {previewEditKey === kNotes ? (
                                    <textarea
                                      id={previewEditDomId(kNotes)}
                                      className="input input-full"
                                      style={{ fontSize: 13, padding: "6px 8px", minHeight: 52, resize: "vertical" }}
                                      rows={2}
                                      value={r.task_notes != null ? String(r.task_notes) : ""}
                                      onChange={(e) => updatePreviewRow(rowIndex, { task_notes: e.target.value })}
                                      onBlur={() => setPreviewEditKey(null)}
                                      onKeyDown={previewEditableKeyDown}
                                      aria-label="Task notes"
                                    />
                                  ) : (
                                    <button
                                      type="button"
                                      className={`ai-preview-read ai-preview-read-notes ${!notesTrim ? "ai-preview-read-muted" : ""}`}
                                      onClick={() => setPreviewEditKey(kNotes)}
                                    >
                                      {notesTrim || "—"}
                                    </button>
                                  )}
                                </td>
                                <td className="ai-preview-col-due">
                                  {previewEditKey === kDue ? (
                                    <input
                                      id={previewEditDomId(kDue)}
                                      type="date"
                                      className="input input-full"
                                      style={{ fontSize: 12, padding: "4px 4px", maxWidth: 128, width: "100%", boxSizing: "border-box" }}
                                      value={dueYmd}
                                      onChange={(e) =>
                                        updatePreviewRow(rowIndex, {
                                          task_due_date: e.target.value ? `${e.target.value}T00:00:00.000Z` : "",
                                        })
                                      }
                                      onBlur={() => setPreviewEditKey(null)}
                                      onKeyDown={previewEditableKeyDown}
                                      aria-label="Due date"
                                    />
                                  ) : (
                                    <button
                                      type="button"
                                      className={`ai-preview-read ${!dueRead ? "ai-preview-read-muted" : ""}`}
                                      onClick={() => setPreviewEditKey(kDue)}
                                    >
                                      {dueRead || "—"}
                                    </button>
                                  )}
                                </td>
                                <td className="ai-preview-col-amount">
                                  {previewEditKey === kAmt ? (
                                    <input
                                      id={previewEditDomId(kAmt)}
                                      type="text"
                                      inputMode="decimal"
                                      autoComplete="off"
                                      className="input input-full"
                                      style={{
                                        fontSize: 12,
                                        padding: "3px 2px",
                                        width: "100%",
                                        maxWidth: "100%",
                                        minWidth: 0,
                                        textAlign: "right",
                                        boxSizing: "border-box",
                                      }}
                                      value={amountFieldDisplayString(r.task_amount)}
                                      onChange={(e) =>
                                        updatePreviewRow(rowIndex, { task_amount: sanitizeAmountTextInput(e.target.value) })
                                      }
                                      onBlur={(e) => {
                                        updatePreviewRow(rowIndex, { task_amount: parseAmountInput(e.target.value) });
                                        setPreviewEditKey(null);
                                      }}
                                      onKeyDown={previewEditableKeyDown}
                                      aria-label="Amount($)"
                                    />
                                  ) : (
                                    <button
                                      type="button"
                                      className="ai-preview-read ai-preview-read-align-right"
                                      onClick={() => setPreviewEditKey(kAmt)}
                                    >
                                      {amountRead}
                                    </button>
                                  )}
                                </td>
                                <td className="ai-preview-col-repeat">
                                  {previewEditKey === kRep ? (
                                    <RepeatPatternPicker
                                      id={previewEditDomId(kRep)}
                                      anchorDate={dueYmd}
                                      value={repeatRule}
                                      onChange={(rule) => {
                                        updatePreviewRow(rowIndex, {
                                          task_repeat_rule: rule,
                                          task_repeat: getRepeatLabel(rule),
                                        });
                                        setPreviewEditKey(null);
                                      }}
                                      onBlur={() => setPreviewEditKey(null)}
                                      compact
                                      dropdownPortal
                                      selectStyle={{ minWidth: 100, maxWidth: 140, fontSize: 12, padding: "4px 6px" }}
                                    />
                                  ) : (
                                    <button type="button" className="ai-preview-read" onClick={() => setPreviewEditKey(kRep)}>
                                      {getRepeatLabel(repeatRule)}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="toolbar">
                    <button type="button" className="btn btn-primary" onClick={handleCreate} disabled={submitting || previewRows.length === 0}>
                      {submitting ? "Importing…" : isExistingProjectMode ? "Add tasks to project" : "Create project & tasks"}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      <DebugTimingsModal
        open={debugTimingsOpen}
        onClose={() => setDebugTimingsOpen(false)}
        title="Generate preview — timing (debug)"
        contextLabel="POST /generateProjectBundleFromText"
        server={meta.debugTimings === true ? { elapsedMs: meta.elapsedMs, fromCache: meta.fromCache } : null}
        client={clientTimings}
      />

      <ConfirmationModal
        open={done}
        mode="alert"
        title={isExistingProjectMode ? "Tasks added" : "Import complete"}
        message={importSuccessModalMessage}
        confirmLabel="View project & tasks"
        onConfirm={handleImportSuccessAck}
      />
    </div>
  );
}

export default AIProjectBuilder;

